import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { useReview, useRevisions, useFindings, useTimeline, usePostComment, useRevisionComparison } from '../hooks/useReviews'
import { useResolveReview } from '../hooks/useResolveReview'
import AskSamiksh, { type BoundAnchor } from '../components/AskSamiksh/AskSamiksh'
import { parseDiff, type DiffRowParsed, type FileDiffParsed } from '../lib/parseDiff'
import { parseASTDiff, type ASTFileParsed } from '../lib/parseASTDiff'
import { ASTDiffViewer } from '../components/ASTDiffViewer/ASTDiffViewer'
import { FindingCard } from '../components/FindingCard/FindingCard'
import { HumanCommentCard } from '../components/FindingCard/HumanCommentCard'
import { SelectionToolbar } from '../components/SelectionToolbar/SelectionToolbar'
import { useDiffSelection, type DiffSelection } from '../hooks/useDiffSelection'
import type { Finding, TimelineEvent } from '../gen/types'

const UI_STORAGE_KEY = 'samiksh.ui.v1'
const ASK_PANE_DEFAULT_WIDTH = 380
const ASK_PANE_MIN_WIDTH = 320
const ASK_PANE_MAX_WIDTH = 720

interface StoredUiState {
  askPane?: { width?: number; collapsed?: boolean }
}

function readStoredUi(): StoredUiState {
  try {
    const raw = localStorage.getItem(UI_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

function writeStoredUi(patch: Partial<StoredUiState>) {
  try {
    const current = readStoredUi()
    const next = { ...current, ...patch }
    localStorage.setItem(UI_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // localStorage unavailable — silently skip persistence
  }
}

type FindingMap = Map<string, Finding[]>

function buildFindingMap(findings: Finding[], revisionId: string | undefined): FindingMap {
  const map: FindingMap = new Map()
  for (const f of findings) {
    if (!f.anchor) continue
    if (revisionId && f.anchor.revisionId && f.anchor.revisionId !== revisionId) continue
    const line = f.anchor.range?.startLine ?? 0
    const key = `${f.anchor.path}:${line}`
    const arr = map.get(key) ?? []
    arr.push(f)
    map.set(key, arr)
  }
  return map
}

interface CommentThread {
  side: 'left' | 'right'
  replies: string[]
  open: boolean
}

type CommentMap = Map<string, CommentThread>

function diffSevClass(sev: string): string {
  if (sev === 'block' || sev === 'blocking') return 'sev-blocking'
  if (sev === 'must-fix' || sev === 'warning') return 'sev-must-fix'
  if (sev === 'suggest') return 'sev-suggest'
  return 'sev-nit'
}

interface FileTreeProps {
  files: FileDiffParsed[]
  collapsed: boolean
  onToggle: () => void
  findingCount: number
}

function FileTree({ files, collapsed, onToggle, findingCount }: FileTreeProps) {
  function scrollToFile(fileId: string) {
    document.getElementById(fileId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div
      className={[
        'shrink-0 bg-page border-r border-border-default overflow-y-auto flex flex-col transition-[width] duration-150 ease-out',
        collapsed ? 'w-[32px] min-w-[32px] overflow-hidden' : 'w-[230px]',
      ].join(' ')}
    >
      <div
        className={[
          'flex items-center gap-1.5 border-b border-border-subtle shrink-0 bg-raised',
          'font-display text-[10px] font-bold uppercase tracking-[0.09em] text-fg-dim',
          collapsed ? 'py-2 justify-center px-0' : 'pl-3 pr-2 py-[7px]',
        ].join(' ')}
      >
        {!collapsed && <span className="flex-1">Changed Files</span>}
        {!collapsed && findingCount > 0 && (
          <span className="font-mono text-[9px] text-sev-blocking border border-sev-blocking/30 bg-sev-blocking/[0.06] rounded-[3px] px-1.5 py-[1px] normal-case tracking-normal font-normal whitespace-nowrap">
            {findingCount} findings
          </span>
        )}
        <button
          className="font-mono text-[13px] text-fg-dim hover:text-fg-muted bg-transparent border-0 cursor-pointer px-0.5 leading-none shrink-0"
          onClick={onToggle}
          title={collapsed ? 'Show files' : 'Hide files'}
        >
          {collapsed ? '›' : '‹'}
        </button>
      </div>
      {!collapsed && (
        <div className="py-1 flex-1">
          {files.map((file, idx) => {
            const name = file.path.split('/').pop() ?? file.path
            const dir = file.path.includes('/')
              ? file.path.slice(0, file.path.lastIndexOf('/') + 1)
              : ''
            return (
              <button
                key={idx}
                onClick={() => scrollToFile(`file-${idx}`)}
                className="w-full flex items-center gap-1.5 px-3 py-1.5 bg-transparent border-0 border-l-2 border-l-transparent text-left font-mono text-[11px] text-fg-muted hover:bg-overlay hover:border-l-border-strong cursor-pointer transition-colors"
              >
                {dir && <span className="text-fg-dim text-[10px] shrink-0">{dir}</span>}
                <span className="flex-1 truncate">{name}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

interface DiffFileSectionProps {
  file: FileDiffParsed
  fileIdx: number
  reviewId: string
  findingMap: FindingMap
  commentsByFinding: Map<string, TimelineEvent[]>
  commentMap: CommentMap
  openCommentKey: string | null
  onLineClick: (path: string, side: 'left' | 'right', lineNum: number) => void
  onCommentCancel: () => void
  onCommentSubmit: (key: string, text: string) => void
  baseLabel: string
  headLabel: string
}

function DiffFileSection({
  file, fileIdx, reviewId, findingMap, commentsByFinding, commentMap, openCommentKey,
  onLineClick, onCommentCancel, onCommentSubmit, baseLabel, headLabel,
}: DiffFileSectionProps) {
  const pathName = file.path.split('/').pop() ?? file.path
  const pathDir = file.path.includes('/') ? file.path.slice(0, file.path.lastIndexOf('/') + 1) : ''

  let fileFindings = 0
  findingMap.forEach((fs, key) => {
    if (key.startsWith(`${file.path}:`)) fileFindings += fs.length
  })

  return (
    <div className="border-b-2 border-border-default" id={`file-${fileIdx}`}>
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-1.5 bg-surface border-b border-border-default">
        <span className="font-mono text-[13px] font-bold text-link flex-1 min-w-0 truncate">
          <span className="text-fg-dim font-normal">{pathDir}</span>
          {pathName}
          {fileFindings > 0 && (
            <span className="ml-2 text-[10px] text-sev-blocking font-normal">
              ⚑ {fileFindings} finding{fileFindings !== 1 ? 's' : ''}
            </span>
          )}
        </span>
      </div>

      <div className="diff-split">
        <div className="diff-side-header side-l">
          <span className="side-label">base</span>
          <span className="side-sha">{baseLabel}</span>
        </div>
        <div className="diff-side-header">
          <span className="side-label">head</span>
          <span className="side-sha">{headLabel}</span>
        </div>

        {file.rows.map((row, rowIdx) => (
          <DiffRowPair
            key={rowIdx}
            row={row}
            path={file.path}
            reviewId={reviewId}
            findingMap={findingMap}
            commentsByFinding={commentsByFinding}
            commentMap={commentMap}
            openCommentKey={openCommentKey}
            onLineClick={onLineClick}
            onCommentCancel={onCommentCancel}
            onCommentSubmit={onCommentSubmit}
          />
        ))}
      </div>
    </div>
  )
}

interface DiffRowPairProps {
  row: DiffRowParsed
  path: string
  reviewId: string
  findingMap: FindingMap
  commentsByFinding: Map<string, TimelineEvent[]>
  commentMap: CommentMap
  openCommentKey: string | null
  onLineClick: (path: string, side: 'left' | 'right', lineNum: number) => void
  onCommentCancel: () => void
  onCommentSubmit: (key: string, text: string) => void
}

function DiffRowPair({
  row, path, reviewId, findingMap, commentsByFinding, commentMap, openCommentKey,
  onLineClick, onCommentCancel, onCommentSubmit,
}: DiffRowPairProps) {
  if (row.kind === 'hunk-header') {
    return <div className="hunk-header">{row.text}</div>
  }

  if (row.kind === 'context') {
    const leftKey = `${path}:left:${row.leftNum}`
    const rightKey = `${path}:right:${row.rightNum}`
    const rightFindings = findingMap.get(`${path}:${row.rightNum}`) ?? []
    const hasLeft = leftKey === openCommentKey || commentMap.has(leftKey)
    const hasRight = rightKey === openCommentKey || commentMap.has(rightKey) || rightFindings.length > 0

    return (
      <>
        <div className="diff-line ctx side-l" data-path={path} data-side="left" data-line={row.leftNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'left', row.leftNum)}>{row.leftNum}</span>
          <span className="line-gutter clickable" onClick={() => onLineClick(path, 'left', row.leftNum)} />
          <span className="line-marker"> </span>
          <span className="line-content">{row.content}</span>
        </div>
        <div className="diff-line ctx" data-path={path} data-side="right" data-line={row.rightNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'right', row.rightNum)}>{row.rightNum}</span>
          <span className="line-gutter clickable" onClick={() => onLineClick(path, 'right', row.rightNum)} />
          <span className="line-marker"> </span>
          <span className="line-content">{row.content}</span>
        </div>
        {(hasLeft || hasRight) && (
          <InlineCards
            path={path}
            reviewId={reviewId}
            leftKey={leftKey}
            rightKey={rightKey}
            leftFindings={[]}
            rightFindings={rightFindings}
            commentsByFinding={commentsByFinding}
            commentMap={commentMap}
            openCommentKey={openCommentKey}
            onCommentCancel={onCommentCancel}
            onCommentSubmit={onCommentSubmit}
          />
        )}
      </>
    )
  }

  if (row.kind === 'del') {
    const leftKey = `${path}:left:${row.leftNum}`
    const leftFindings = findingMap.get(`${path}:${row.leftNum}`) ?? []
    const hasLeft = leftKey === openCommentKey || commentMap.has(leftKey) || leftFindings.length > 0

    return (
      <>
        <div className="diff-line del side-l" data-path={path} data-side="left" data-line={row.leftNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'left', row.leftNum)}>{row.leftNum}</span>
          <span className="line-gutter clickable" onClick={() => onLineClick(path, 'left', row.leftNum)} />
          <span className="line-marker">−</span>
          <span className="line-content">{row.content}</span>
        </div>
        <div className="diff-line empty">
          <span className="line-num" />
          <span className="line-gutter" />
          <span className="line-marker" />
          <span className="line-content" />
        </div>
        {hasLeft && (
          <InlineCards
            path={path}
            reviewId={reviewId}
            leftKey={leftKey}
            rightKey={`${path}:right:0`}
            leftFindings={leftFindings}
            rightFindings={[]}
            commentsByFinding={commentsByFinding}
            commentMap={commentMap}
            openCommentKey={openCommentKey}
            onCommentCancel={onCommentCancel}
            onCommentSubmit={onCommentSubmit}
          />
        )}
      </>
    )
  }

  if (row.kind === 'add') {
    const rightKey = `${path}:right:${row.rightNum}`
    const rightFindings = findingMap.get(`${path}:${row.rightNum}`) ?? []
    const hasRight = rightKey === openCommentKey || commentMap.has(rightKey) || rightFindings.length > 0

    return (
      <>
        <div className="diff-line empty side-l">
          <span className="line-num" />
          <span className="line-gutter" />
          <span className="line-marker" />
          <span className="line-content" />
        </div>
        <div className="diff-line add" data-path={path} data-side="right" data-line={row.rightNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'right', row.rightNum)}>{row.rightNum}</span>
          <span className="line-gutter clickable" onClick={() => onLineClick(path, 'right', row.rightNum)} />
          <span className="line-marker">+</span>
          <span className="line-content">{row.content}</span>
        </div>
        {hasRight && (
          <InlineCards
            path={path}
            reviewId={reviewId}
            leftKey={`${path}:left:0`}
            rightKey={rightKey}
            leftFindings={[]}
            rightFindings={rightFindings}
            commentsByFinding={commentsByFinding}
            commentMap={commentMap}
            openCommentKey={openCommentKey}
            onCommentCancel={onCommentCancel}
            onCommentSubmit={onCommentSubmit}
          />
        )}
      </>
    )
  }

  if (row.kind === 'pair') {
    const leftKey = `${path}:left:${row.leftNum}`
    const rightKey = `${path}:right:${row.rightNum}`
    const rightFindings = findingMap.get(`${path}:${row.rightNum}`) ?? []
    const hasLeft = leftKey === openCommentKey || commentMap.has(leftKey)
    const hasRight = rightKey === openCommentKey || commentMap.has(rightKey) || rightFindings.length > 0

    return (
      <>
        <div className="diff-line del side-l" data-path={path} data-side="left" data-line={row.leftNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'left', row.leftNum)}>{row.leftNum}</span>
          <span className="line-gutter clickable" onClick={() => onLineClick(path, 'left', row.leftNum)} />
          <span className="line-marker">−</span>
          <span className="line-content">{row.leftContent}</span>
        </div>
        <div className="diff-line add" data-path={path} data-side="right" data-line={row.rightNum}>
          <span className="line-num" onClick={() => onLineClick(path, 'right', row.rightNum)}>{row.rightNum}</span>
          <span className="line-gutter clickable" onClick={() => onLineClick(path, 'right', row.rightNum)} />
          <span className="line-marker">+</span>
          <span className="line-content">{row.rightContent}</span>
        </div>
        {(hasLeft || hasRight) && (
          <InlineCards
            path={path}
            reviewId={reviewId}
            leftKey={leftKey}
            rightKey={rightKey}
            leftFindings={[]}
            rightFindings={rightFindings}
            commentsByFinding={commentsByFinding}
            commentMap={commentMap}
            openCommentKey={openCommentKey}
            onCommentCancel={onCommentCancel}
            onCommentSubmit={onCommentSubmit}
          />
        )}
      </>
    )
  }

  return null
}

interface InlineCardsProps {
  path: string
  reviewId: string
  leftKey: string
  rightKey: string
  leftFindings: Finding[]
  rightFindings: Finding[]
  commentsByFinding: Map<string, TimelineEvent[]>
  commentMap: CommentMap
  openCommentKey: string | null
  onCommentCancel: () => void
  onCommentSubmit: (key: string, text: string) => void
}

function InlineCards({
  reviewId, leftKey, rightKey, leftFindings, rightFindings,
  commentsByFinding, commentMap, openCommentKey, onCommentCancel, onCommentSubmit,
}: InlineCardsProps) {
  const hasLeft = leftFindings.length > 0 || leftKey === openCommentKey || commentMap.has(leftKey)
  const hasRight = rightFindings.length > 0 || rightKey === openCommentKey || commentMap.has(rightKey)

  if (!hasLeft && !hasRight) return null

  return (
    <>
      {hasLeft ? (
        <div className="inline-card-wrap side-l">
          {leftFindings.map((f) => (
            <div
              key={f.id}
              className={[
                'inline-finding',
                diffSevClass(f.severity),
                f.status === 'resolved' ? 'resolved' : '',
              ].join(' ')}
            >
              <div className="finding-gutter" />
              <FindingCard
                hideAccent
                finding={f}
                reviewId={reviewId}
                comments={commentsByFinding.get(f.id) ?? []}
                isResolved={f.status === 'resolved'}
              />
            </div>
          ))}
          {(commentMap.has(leftKey) || leftKey === openCommentKey) && (
            <div className="inline-finding">
              <div className="finding-gutter" />
              <HumanCommentCard
                replies={commentMap.get(leftKey)?.replies ?? []}
                isOpen={leftKey === openCommentKey}
                onSubmit={(text) => onCommentSubmit(leftKey, text)}
                onCancel={onCommentCancel}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="finding-spacer side-l" />
      )}

      {hasRight ? (
        <div className="inline-card-wrap">
          {rightFindings.map((f) => (
            <div
              key={f.id}
              className={[
                'inline-finding',
                diffSevClass(f.severity),
                f.status === 'resolved' ? 'resolved' : '',
              ].join(' ')}
            >
              <div className="finding-gutter" />
              <FindingCard
                hideAccent
                finding={f}
                reviewId={reviewId}
                comments={commentsByFinding.get(f.id) ?? []}
                isResolved={f.status === 'resolved'}
              />
            </div>
          ))}
          {(commentMap.has(rightKey) || rightKey === openCommentKey) && (
            <div className="inline-finding">
              <div className="finding-gutter" />
              <HumanCommentCard
                replies={commentMap.get(rightKey)?.replies ?? []}
                isOpen={rightKey === openCommentKey}
                onSubmit={(text) => onCommentSubmit(rightKey, text)}
                onCancel={onCommentCancel}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="finding-spacer" />
      )}
    </>
  )
}

export default function ReviewDiffPage() {
  const { tenant, repo, reviewName } = useParams<{ tenant: string; repo: string; reviewName: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  // Resolve the URL triplet to a reviewId once; downstream hooks keep keying
  // by reviewId.
  const resolved = useResolveReview(tenant, repo, reviewName)
  const reviewId = resolved.data?.reviewId ?? ''

  const { data: review } = useReview(reviewId)
  const { data: revisions = [] } = useRevisions(reviewId)
  const { data: findings = [] } = useFindings(reviewId)
  const { data: timelineEvents = [] } = useTimeline(reviewId)
  const postComment = usePostComment(reviewId)

  const urlRevId = searchParams.get('rev')
  const urlBaseId = searchParams.get('base')
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(urlRevId)
  // null  → fall back to the active revision's direct parent (or PR base if r1).
  //         The default isn't stored as a sentinel; we resolve it on the fly so
  //         switching HEAD always lands on a sensible default.
  // ''    → diff against the PR base SHA explicitly.
  // other → a revision id (must have seq < activeRevision.seq).
  const [selectedBaseRevisionId, setSelectedBaseRevisionId] = useState<string | null>(urlBaseId)

  useEffect(() => {
    if (revisions.length === 0) return
    if (selectedRevisionId && revisions.some((r) => r.id === selectedRevisionId)) return
    const latest = revisions[revisions.length - 1]
    if (latest) {
      setSelectedRevisionId(latest.id)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('rev', latest.id)
          return next
        },
        { replace: true },
      )
    }
  }, [revisions]) // eslint-disable-line react-hooks/exhaustive-deps

  const activeRevision = revisions.find((r) => r.id === selectedRevisionId) ?? revisions[revisions.length - 1]

  const updateUrlParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value === null) next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const handleSelectRevision = useCallback(
    (revId: string) => {
      setSelectedRevisionId(revId)
      updateUrlParam('rev', revId)
      // Preserve the user's BASE selection across HEAD switches. If the chosen
      // base is no longer valid for the new HEAD (e.g. its seq >= new head's
      // seq), the effect below drops it back to the default.
    },
    [updateUrlParam],
  )

  const handleSelectBase = useCallback(
    (raw: string) => {
      setSelectedBaseRevisionId(raw)
      updateUrlParam('base', raw)
    },
    [updateUrlParam],
  )

  // Available base options: revisions strictly older than HEAD (by seq), sorted
  // by seq asc, with two filters:
  //   - exclude any revision whose SHA equals the active HEAD's SHA (would
  //     produce an empty diff and confuse the user)
  //   - dedupe by SHA: when multiple revisions share a commit (e.g. duplicate
  //     webhook deliveries), keep the lowest-seq one
  const baseOptions = useMemo(() => {
    if (!activeRevision) return []
    const seenSha = new Set<string>([activeRevision.sha])
    const out: typeof revisions = []
    for (const r of [...revisions].sort((a, b) => a.seq - b.seq)) {
      if (r.seq >= activeRevision.seq) continue
      if (seenSha.has(r.sha)) continue
      seenSha.add(r.sha)
      out.push(r)
    }
    return out
  }, [revisions, activeRevision])

  // Default base when the user hasn't chosen one: PR base SHA. This matches the
  // semantic of the stored Revision.diffFromParent (despite its name, the
  // workflow stores the *full PR diff* from PR base → revision head, fetched
  // via GitHub's PR diff endpoint — see internal/workflow/review.go).
  // '' = PR base.
  const effectiveBaseId: string = selectedBaseRevisionId ?? ''

  // Drop a stale ?base= if the user-selected base no longer applies to the
  // current head (different revision tree after switching HEAD).
  useEffect(() => {
    if (selectedBaseRevisionId === null || selectedBaseRevisionId === '') return
    const stillValid = baseOptions.some((r) => r.id === selectedBaseRevisionId)
    if (!stillValid) {
      setSelectedBaseRevisionId(null)
      updateUrlParam('base', null)
    }
  }, [selectedBaseRevisionId, baseOptions, updateUrlParam])

  // Fast path: when base = PR base, reuse the stored diffFromParent + astDiffJson
  // (which is exactly PR-base → revision-head). For any other base we must hit
  // the RPC since no revision-to-revision diff is stored.
  const useStoredDiff = effectiveBaseId === ''

  const comparison = useRevisionComparison(
    reviewId,
    activeRevision?.id ?? null,
    useStoredDiff ? null : effectiveBaseId,
  )

  const parsedFiles = useMemo(() => {
    if (useStoredDiff) return parseDiff(activeRevision?.diffFromParent ?? '')
    return parseDiff(comparison.data?.unifiedDiff ?? '')
  }, [useStoredDiff, comparison.data, activeRevision])

  const astFiles = useMemo(() => {
    if (useStoredDiff) return parseASTDiff(activeRevision?.astDiffJson)
    return parseASTDiff(comparison.data?.astDiffJson || undefined)
  }, [useStoredDiff, comparison.data, activeRevision])

  const astFileMap = useMemo(() => {
    const m = new Map<string, ASTFileParsed>()
    for (const f of astFiles) m.set(f.path, f)
    return m
  }, [astFiles])

  const revisionFindings = useMemo(() => {
    if (!activeRevision) return findings
    return findings.filter((f) => !f.anchor?.revisionId || f.anchor.revisionId === activeRevision.id)
  }, [findings, activeRevision])

  const findingMap = useMemo(
    () => buildFindingMap(revisionFindings, activeRevision?.id),
    [revisionFindings, activeRevision],
  )

  const commentsByFinding = useMemo(() => {
    const map = new Map<string, TimelineEvent[]>()
    for (const event of timelineEvents) {
      if (event.kind !== 'human-comment' || !event.findingId) continue
      const arr = map.get(event.findingId) ?? []
      arr.push(event)
      map.set(event.findingId, arr)
    }
    return map
  }, [timelineEvents])

  const [treeCollapsed, setTreeCollapsed] = useState(false)
  const [commentMap, setCommentMap] = useState<CommentMap>(new Map())
  const [openCommentKey, setOpenCommentKey] = useState<string | null>(null)

  const initialUi = useMemo(() => readStoredUi(), [])
  const [askPaneCollapsed, setAskPaneCollapsed] = useState<boolean>(initialUi.askPane?.collapsed ?? false)
  const [askPaneWidth, setAskPaneWidth] = useState<number>(() => {
    const w = initialUi.askPane?.width
    if (typeof w === 'number' && w >= ASK_PANE_MIN_WIDTH && w <= ASK_PANE_MAX_WIDTH) return w
    return ASK_PANE_DEFAULT_WIDTH
  })
  const [boundAnchor, setBoundAnchor] = useState<BoundAnchor | null>(null)

  useEffect(() => {
    writeStoredUi({ askPane: { width: askPaneWidth, collapsed: askPaneCollapsed } })
  }, [askPaneWidth, askPaneCollapsed])

  const diffContainerRef = useRef<HTMLDivElement>(null)
  const askInputRef = useRef<HTMLInputElement>(null)
  const selection = useDiffSelection(diffContainerRef)

  const commentMapInitialized = useRef(false)
  useEffect(() => {
    if (commentMapInitialized.current || timelineEvents.length === 0) return
    const map: CommentMap = new Map()
    for (const ev of timelineEvents) {
      if (ev.kind !== 'human-comment' || ev.findingId || !ev.anchor?.path) continue
      const { path, revisionId, range } = ev.anchor
      const line = range?.startLine ?? 0
      const side: 'left' | 'right' = revisionId ? 'right' : 'left'
      const key = `${path}:${side}:${line}`
      const thread = map.get(key) ?? { side, replies: [], open: false }
      thread.replies = [...thread.replies, ev.message ?? '']
      map.set(key, thread)
    }
    if (map.size > 0) {
      setCommentMap(map)
      commentMapInitialized.current = true
    }
  }, [timelineEvents])

  const urlFile = searchParams.get('file')
  useEffect(() => {
    if (!urlFile || parsedFiles.length === 0) return
    const idx = parsedFiles.findIndex((f) => f.path === urlFile)
    if (idx === -1) return
    const el = document.getElementById(`file-${idx}`)
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100)
    }
  }, [urlFile, parsedFiles])

  const handleLineClick = useCallback((path: string, side: 'left' | 'right', lineNum: number) => {
    const key = `${path}:${side}:${lineNum}`
    setOpenCommentKey((prev) => (prev === key ? null : key))
  }, [])

  const handleCommentCancel = useCallback(() => setOpenCommentKey(null), [])

  const handleCommentSubmit = useCallback((key: string, text: string) => {
    const parts = key.split(':')
    const lineNum = parseInt(parts[parts.length - 1], 10)
    const side = parts[parts.length - 2] as 'left' | 'right'
    const path = parts.slice(0, -2).join(':')

    setCommentMap((prev) => {
      const next = new Map(prev)
      const existing = next.get(key)
      next.set(key, { side, replies: [...(existing?.replies ?? []), text], open: true })
      return next
    })
    postComment.mutate({
      message: text,
      anchor: {
        path,
        revisionId: side === 'right' ? (selectedRevisionId ?? '') : '',
        range: { startLine: lineNum, endLine: lineNum },
      },
    })
  }, [postComment, selectedRevisionId])

  const findingCount = revisionFindings.length

  const clearNativeSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges()
  }, [])

  const handleAsk = useCallback((sel: DiffSelection) => {
    setBoundAnchor({ path: sel.path, startLine: sel.startLine, endLine: sel.endLine })
    setAskPaneCollapsed(false)
    clearNativeSelection()
    setTimeout(() => askInputRef.current?.focus(), 0)
  }, [clearNativeSelection])

  const handleSelectionComment = useCallback((sel: DiffSelection) => {
    handleLineClick(sel.path, sel.side, sel.startLine)
    clearNativeSelection()
  }, [handleLineClick, clearNativeSelection])

  const handleCopyLink = useCallback(
    async (sel: DiffSelection) => {
      const params = new URLSearchParams()
      if (selectedRevisionId) params.set('rev', selectedRevisionId)
      params.set('file', sel.path)
      const lineHash =
        sel.startLine === sel.endLine
          ? `#L${sel.startLine}`
          : `#L${sel.startLine}-L${sel.endLine}`
      // Build the shareable URL using the human-friendly triplet so it doesn't
      // leak the internal review uuid. Falls back to /reviews/<id> only if the
      // review resolver hasn't completed yet (unlikely on the diff page).
      const url = review
        ? `${window.location.origin}/${review.tenantName}/${review.repoName}/reviews/${review.name}/diff?${params.toString()}${lineHash}`
        : `${window.location.origin}/reviews/${reviewId}/diff?${params.toString()}${lineHash}`
      try {
        await navigator.clipboard.writeText(url)
      } catch {
        // Clipboard unavailable; silently no-op
      }
      clearNativeSelection()
    },
    [reviewId, selectedRevisionId, clearNativeSelection],
  )

  const handlePaneResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      const startX = e.clientX
      const startW = askPaneWidth
      const handle = e.currentTarget
      handle.setPointerCapture(e.pointerId)
      const onMove = (ev: PointerEvent) => {
        const dx = startX - ev.clientX
        const next = Math.max(
          ASK_PANE_MIN_WIDTH,
          Math.min(ASK_PANE_MAX_WIDTH, startW + dx),
        )
        setAskPaneWidth(next)
      }
      const onUp = (ev: PointerEvent) => {
        try {
          handle.releasePointerCapture(ev.pointerId)
        } catch {
          /* pointer may already be released */
        }
        handle.removeEventListener('pointermove', onMove)
        handle.removeEventListener('pointerup', onUp)
      }
      handle.addEventListener('pointermove', onMove)
      handle.addEventListener('pointerup', onUp)
    },
    [askPaneWidth],
  )

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-canvas relative">
      {/* Diff top bar */}
      <div className="flex items-center h-[42px] shrink-0 bg-surface border-b border-border-default">
        <Link
          to={review ? `/${review.tenantName}/${review.repoName}/reviews/${review.name}` : `/reviews/${reviewId}`}
          className="flex items-center gap-1.5 font-mono text-[11px] text-fg-dim hover:text-fg-muted hover:bg-overlay no-underline whitespace-nowrap shrink-0 px-4 h-full border-r border-border-subtle transition-colors"
        >
          ← {review ? `${review.tenantName}/${review.repoName} #${review.prNumber}` : 'Back'}
        </Link>

        {review && (
          <div className="flex items-center px-4 border-r border-border-subtle max-w-[320px] shrink-0">
            <span className="text-[12px] font-semibold text-fg-muted truncate">
              {review.prTitle}
            </span>
          </div>
        )}

        <div className="flex items-center flex-1">
          <div className="flex items-center gap-2 px-3.5 flex-1 justify-end border-r border-border-subtle">
            <span className="font-mono text-[10px] font-bold text-fg-dim uppercase tracking-[0.05em] whitespace-nowrap shrink-0">
              base
            </span>
            <select
              className="select font-mono text-[12px] py-[3px] px-2 min-w-[180px]"
              value={effectiveBaseId}
              onChange={(e) => handleSelectBase(e.target.value)}
              disabled={!activeRevision}
            >
              <option value="">PR base · {review?.baseSha.slice(0, 7) ?? ''}</option>
              {baseOptions.map((rev) => (
                <option key={rev.id} value={rev.id}>
                  r{rev.seq} · {rev.sha.slice(0, 7)}
                  {rev.summary ? ` — ${rev.summary.slice(0, 50)}` : ''}
                </option>
              ))}
            </select>
            {!useStoredDiff && comparison.isFetching && (
              <span className="font-mono text-[10px] text-fg-dim shrink-0">loading…</span>
            )}
          </div>
          <span className="font-mono text-[11px] text-fg-dim px-3 shrink-0">↔</span>
          <div className="flex items-center gap-2 px-3.5 flex-1">
            <span className="font-mono text-[10px] font-bold text-fg-dim uppercase tracking-[0.05em] whitespace-nowrap shrink-0">
              head
            </span>
            <select
              className="select font-mono text-[12px] py-[3px] px-2 min-w-[180px]"
              value={selectedRevisionId ?? ''}
              onChange={(e) => handleSelectRevision(e.target.value)}
            >
              {revisions.map((rev) => (
                <option key={rev.id} value={rev.id}>
                  r{rev.seq} · {rev.sha.slice(0, 7)}
                  {rev.summary ? ` — ${rev.summary.slice(0, 50)}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Workspace */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        <FileTree
          files={parsedFiles}
          collapsed={treeCollapsed}
          onToggle={() => setTreeCollapsed((c) => !c)}
          findingCount={findingCount}
        />

        <div
          ref={diffContainerRef}
          data-testid="diff-scroller"
          className="flex-1 overflow-auto bg-canvas pb-12 shadow-[inset_2px_0_12px_rgba(0,0,0,0.35)]"
          onClick={(e) => {
            const target = e.target as HTMLElement
            if (
              !target.closest(
                '.inline-card-wrap, .inline-finding, .diff-line',
              )
            ) {
              setOpenCommentKey(null)
            }
          }}
        >
          {parsedFiles.length === 0 && (
            <div className="py-10 px-6 text-center text-[13px]">
              {!activeRevision && (
                <span className="text-fg-dim">Select a revision to view the diff.</span>
              )}
              {activeRevision && !useStoredDiff && comparison.isError && (
                <div className="text-fg-dim">
                  <div className="text-danger mb-2">Could not compute diff between selected revisions.</div>
                  <div className="font-mono text-[11px] whitespace-pre-wrap break-all">
                    {comparison.error instanceof Error ? comparison.error.message : String(comparison.error)}
                  </div>
                </div>
              )}
              {activeRevision && !useStoredDiff && !comparison.isError && !comparison.isFetching && (
                <span className="text-fg-dim">No changes between selected revisions.</span>
              )}
              {activeRevision && useStoredDiff && (
                <span className="text-fg-dim">No diff for this revision.</span>
              )}
            </div>
          )}

          {parsedFiles.map((file, idx) => {
            const astFile = astFileMap.get(file.path)
            const baseSha = useStoredDiff
              ? review?.baseSha
              : comparison.data?.baseSha
            const baseLabel = baseSha?.slice(0, 7) ?? 'base'
            const headLabel = activeRevision?.sha.slice(0, 7) ?? 'head'
            const sharedSectionProps = {
              fileIdx: idx,
              reviewId,
              findingMap,
              commentsByFinding,
              commentMap,
              openCommentKey,
              onLineClick: handleLineClick,
              onCommentCancel: handleCommentCancel,
              onCommentSubmit: handleCommentSubmit,
              baseLabel,
              headLabel,
            }
            if (astFile) {
              return (
                <ASTDiffViewer
                  key={idx}
                  astFile={astFile}
                  unifiedFile={file}
                  fileIdx={idx}
                  reviewId={reviewId}
                  findingMap={findingMap}
                  commentMap={commentMap}
                  openCommentKey={openCommentKey}
                  onLineClick={handleLineClick}
                  baseLabel={baseLabel}
                  headLabel={headLabel}
                  renderInlineCards={(leftKey, rightKey, leftFindings, rightFindings) => (
                    <InlineCards
                      path={file.path}
                      reviewId={reviewId}
                      leftKey={leftKey}
                      rightKey={rightKey}
                      leftFindings={leftFindings}
                      rightFindings={rightFindings}
                      commentsByFinding={commentsByFinding}
                      commentMap={commentMap}
                      openCommentKey={openCommentKey}
                      onCommentCancel={handleCommentCancel}
                      onCommentSubmit={handleCommentSubmit}
                    />
                  )}
                  renderUnifiedFallback={() => (
                    <DiffFileSection file={file} {...sharedSectionProps} />
                  )}
                />
              )
            }
            return <DiffFileSection key={idx} file={file} {...sharedSectionProps} />
          })}
        </div>

        {/* Ask samik.sh right-docked pane */}
        {askPaneCollapsed ? (
          <div className="w-[36px] shrink-0 bg-page border-l border-border-default flex flex-col items-center pt-2">
            <button
              type="button"
              data-testid="ask-pane-toggle"
              title="Open Ask samik.sh"
              aria-label="Open Ask samik.sh"
              onClick={() => setAskPaneCollapsed(false)}
              className="w-[28px] h-[28px] flex items-center justify-center bg-transparent border border-border-subtle rounded-md text-accent hover:bg-overlay cursor-pointer"
            >
              ⚡
            </button>
          </div>
        ) : (
          <div
            data-testid="ask-pane"
            style={{ width: askPaneWidth }}
            className="shrink-0 bg-raised border-l border-border-default flex flex-col min-h-0 relative"
          >
            <div
              onPointerDown={handlePaneResizeStart}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize Ask samik.sh pane"
              className="absolute top-0 left-0 h-full w-[6px] -translate-x-[3px] cursor-col-resize z-10 hover:bg-accent/30"
            />
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle bg-page shrink-0">
              <span aria-hidden className="text-[13px] text-accent">⚡</span>
              <span className="font-display text-[12px] font-semibold text-fg flex-1">
                Ask samik.sh
              </span>
              <span className="font-mono text-[10px] text-fg-dim hidden sm:inline">
                Enter to send · Shift+Enter for newline
              </span>
              <button
                type="button"
                data-testid="ask-pane-toggle"
                onClick={() => setAskPaneCollapsed(true)}
                title="Collapse pane"
                aria-label="Collapse Ask samik.sh"
                className="bg-transparent border-0 text-fg-dim hover:text-fg cursor-pointer px-1 text-[14px] leading-none"
              >
                ›
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              <AskSamiksh
                reviewId={reviewId}
                revisionId={activeRevision?.id}
                boundAnchor={boundAnchor}
                onClearBoundAnchor={() => setBoundAnchor(null)}
                inputRef={askInputRef}
              />
            </div>
          </div>
        )}
      </div>

      <SelectionToolbar
        selection={selection}
        onAsk={handleAsk}
        onComment={handleSelectionComment}
        onCopyLink={handleCopyLink}
      />
    </div>
  )
}
